import React from "react";
import ServicePlanStore from "../ServicePlanStore";
import { observer, inject } from "mobx-react";
import { CmsTab } from "../models/ServicePlanModels";

export interface ServicePlanPriceTableProps {
  selectedModel: string;
  isServicePlan: Boolean;
  handleModelVariantChange: Function;
}
interface InjectedProps extends ServicePlanPriceTableProps {
  servicePlanStore: ServicePlanStore;
}

/**
 * Dynamically filters interval rows: a row is shown only when at least one
 * variant whose GUID is included in the active tab (or the provided guidsFilter)
 * is NOT marked as "not applicable".
 */
function filterIntervalsByVariantGuids(intervalData: any[], guidsFilter: Set<string>) {
  if (guidsFilter.size === 0) return intervalData;
  return intervalData.filter((interval) =>
    interval.modelVariantServicePrices.some(
      (mvsp: any) => (
        guidsFilter.has((mvsp.variantGUID as string || "").trim().toUpperCase()) ||
        (mvsp.pimIdentifier && guidsFilter.has((mvsp.pimIdentifier as string || "").trim().toUpperCase()))
      ) && !mvsp.isNotApplicable
    )
  );
}

const ServicePlanPriceTable: React.SFC<ServicePlanPriceTableProps> = (props) => {
  let injectedProps = props as InjectedProps;
  const store = injectedProps.servicePlanStore;

  const cmsVehicleModels = props.isServicePlan
    ? store.servicePlanCMSModel.response
    : store.cappedPricingCMSModel.response;

  const vehicleApiModels = props.isServicePlan
    ? store.servicePlan.response
    : store.cappedPricing.response;

  const vehicleVariantCappedServiceTotal = props.isServicePlan
    ? store.variantCappedServiceTotalModel.response
    : null;

  const selectedModelVariant = store.modelVariantsSelected;

  const activeTab: CmsTab | null = props.isServicePlan
    ? store.selectedServicePlanTab
    : store.selectedCappedPricingTab;

  if (!activeTab) {
    return null;
  }

  const apiModelId = activeTab.modelId;

  const apiModel = vehicleApiModels.find(
    (m) => m.id.toLocaleLowerCase() === (apiModelId ?? "").toLocaleLowerCase()
  );

  let intervalData: any[] = apiModel?.intervalData ?? [];

  let modelServicePlanContent = activeTab.modelServicePlanContent ?? "";
  let modelCappedPriceServiceContent = activeTab.modelCappedPriceServiceContent ?? "";

  const tab = activeTab;
  const isHiddenBehindButton = tab.displayStyle === "Hidden Behind Button";

  const colLabels = tab.columns.map(col => 
    col.columnHeading 
      ? col.columnHeading.replace(/<\/?p[^>]*>/g, "") 
      : col.variants.map((v) => v.variantName).filter(name => !!name).join(" / ")
  );

  let activeLabel = store.modelVariantsSelected;
  if (activeLabel && !colLabels.includes(activeLabel)) {
    activeLabel = ""; // Reset if it doesn't match the current tab
  }

  const visibleColumns = isHiddenBehindButton && activeLabel
    ? tab.columns.filter((col, i) => colLabels[i] === activeLabel) 
    : tab.columns;

  // Filter intervals to only show rows relevant to the visible columns!
  const visibleVariantGuids = new Set(
    visibleColumns.flatMap(col => col.variants.flatMap(v => [
      (v.variantGuid || "").trim().toUpperCase(),
      (v.pimIdentifier || "").trim().toUpperCase()
    ])).filter(g => !!g)
  );
  intervalData = filterIntervalsByVariantGuids(intervalData, visibleVariantGuids);

  return (
    <div id="servicePlanPriceInterval">
      {isHiddenBehindButton && (
        <div className="cappedPricingTableHeading text-center">
          <div className="cta_wrapper">
            <h5>
              {tab.buttonText || `Please select your ${apiModel?.name || "vehicle"} variant`}
            </h5>
            {colLabels.map((label, i) => (
              <a
                key={i}
                className={activeLabel !== label ? "cta-button" : ""}
                onClick={(e) => {
                  e.preventDefault();
                  store.setModelVariantSelected(label);
                }}
                style={{ cursor: "pointer" }}
              >
                {label}
              </a>
            ))}
          </div>
        </div>
      )}
      
      {(!isHiddenBehindButton || activeLabel) && (
        <>
          <table className="priceTable__table">
            <thead>
              <tr className="priceTable__tr">
                <th
                  className="priceTable__th alignCenter"
                  style={{ minWidth: "270px" }}
                >
                  Scheduled Service Intervals
                  <sup>{props.isServicePlan ? 2 : 1}</sup>
                </th>
                {visibleColumns.map((col, colIdx) => (
                  <th key={colIdx} className="priceTable__th alignCenter">
                    {col.columnHeading ? (
                      <span dangerouslySetInnerHTML={{ __html: col.columnHeading.replace(/<\/?p[^>]*>/g, "") }} />
                    ) : (
                      col.variants.map((v) => v.variantName).filter(name => !!name).join(" / ")
                    )}
                    <sup>{props.isServicePlan ? 3 : 2}</sup>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {intervalData.map((intervalItem, i) => {
                return [
                  <tr key={i} className="priceTable__tr">
                    <td className="priceTable__td alignCenter">
                      <span
                        dangerouslySetInnerHTML={{
                          __html: intervalItem.name.replace("|", "</br>"),
                        }}
                      />
                    </td>
                    {visibleColumns.map((col, colIdx) => {
                      // Aggregate all prices across variants in this column.
                      const colVariantGuids = new Set(
                        col.variants.flatMap((v) => [
                          (v.variantGuid || "").trim().toUpperCase(),
                          (v.pimIdentifier || "").trim().toUpperCase()
                        ]).filter(g => !!g)
                      );
                      const prices = intervalItem.modelVariantServicePrices.filter(
                        (mvsp: any) =>
                          colVariantGuids.has((mvsp.variantGUID as string || "").trim().toUpperCase())
                      );
                      if (prices.length === 0) return <td key={colIdx} className="priceTable__td alignCenter">N/A</td>;
                      const isNA = prices.every((p: any) => p.isNotApplicable);
                      const isFree = !isNA && prices.every((p: any) => p.price === 0);
                      const totalPrice = prices.reduce((sum: number, p: any) => sum + (p.isNotApplicable ? 0 : p.price), 0);
                      const priceString = isNA ? "N/A" : isFree ? "Free" : `$${totalPrice.toFixed(2)}`;
                      return (
                        <td key={colIdx} className="priceTable__td alignCenter">
                          {priceString}
                        </td>
                      );
                    })}
                  </tr>,
                  // Total rows for service plan (3-year and 5-year)
                  props.isServicePlan &&
                    (intervalItem.name.includes("36 months") ||
                      intervalData.length === i + 1) && (
                      <tr key={"total" + i} className="priceTable__tr">
                        <td className="priceTable__td priceTable__td--price alignCenter">
                          Total {intervalItem.name.includes("36 months") ? "3" : "5"} Year Service Plan Price (including GST)
                        </td>
                        {visibleColumns.map((col, colIdx) => {
                          const colVariantGuids = new Set(
                            col.variants.flatMap((v) => [
                              (v.variantGuid || "").trim().toUpperCase(),
                              (v.pimIdentifier || "").trim().toUpperCase()
                            ]).filter(g => !!g)
                          );
                          const totalsForCol = (vehicleVariantCappedServiceTotal || []).filter(
                            (t) => colVariantGuids.has((t.variantGUID || "").trim().toUpperCase())
                          );
                          const total = intervalItem.name.includes("36 months")
                            ? totalsForCol.reduce((sum, t) => sum + t.threeYearsPrice, 0)
                            : totalsForCol.reduce((sum, t) => sum + t.totalPrice, 0);
                          return (
                            <td key={colIdx} className="priceTable__td priceTable__td--price alignCenter">
                              {totalsForCol.length > 0 ? `$${total.toFixed(2)}` : "-"}
                            </td>
                          );
                        })}
                      </tr>
                    ),
                ];
              })}
            </tbody>
          </table>

          <div className="pricingDisclaimer">All prices include GST</div>
          {isHiddenBehindButton && visibleColumns.some(col => !!col.columnContent)
            ? visibleColumns.map((col, i) =>
                col.columnContent
                  ? <div key={i} dangerouslySetInnerHTML={{ __html: col.columnContent }} />
                  : null
              )
            : <div
                dangerouslySetInnerHTML={{
                  __html: props.isServicePlan
                    ? modelServicePlanContent
                    : modelCappedPriceServiceContent,
                }}
              />
          }
        </>
      )}
    </div>
  );
};

export default inject("servicePlanStore")(observer(ServicePlanPriceTable));
